// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState } from 'react';

import type { SurveyType } from '../../../api/types';
import { ROOM_SHARE_LIMIT, roomShares, topRoomShares } from '../../../bygning/parts';
import { formatNumber } from '../../../kortlaegning/vocab';
import { TextLink } from '../../TextLink';
import styles from './Inspector.module.css';

export interface RoomChipsProps {
  type: SurveyType;
  /** The focused room (`?room=`). */
  focused: number | null;
  onFocus: (room: number | null) => void;
}

/**
 * "Fordeling på rum": one chip per room with the type's share there; a click
 * focuses the room (again: unfocus). A type in many rooms shows its largest
 * `ROOM_SHARE_LIMIT` (and the focused room, wherever it ranks), the rest
 * behind "+ n flere" (`topRoomShares`); a few rooms show whole, in room order.
 */
export function RoomChips({ type, focused, onFocus }: RoomChipsProps) {
  // Expanded for this type only, so the next type starts collapsed.
  const [expandedFor, setExpandedFor] = useState<number | null>(null);
  const rooms = roomShares(type);
  if (rooms.length === 0) return <span className={`${styles.text} ${styles.textMissing}`}>Ingen rum</span>;
  const stk = type.unit === 'stk';
  const { shown, hidden } = topRoomShares(rooms, ROOM_SHARE_LIMIT, focused);
  const expanded = expandedFor === type.id;
  const list = expanded ? [...shown, ...hidden] : shown;
  return (
    <div className={styles.chips} role="group" aria-label="Fordeling på rum">
      {list.map((r) => (
        <button
          key={r.id ?? `n${r.name}`}
          type="button"
          className={styles.room}
          aria-pressed={r.id !== null && focused === r.id}
          disabled={r.id === null}
          title={r.id !== null ? `Fokusér ${r.name}` : undefined}
          onClick={() => r.id !== null && onFocus(focused === r.id ? null : r.id)}
        >
          {r.name}{' '}
          <span className={styles.chipMuted}>{stk ? `${formatNumber(r.quantity)} stk` : `≈ ${formatNumber(r.quantity)} ${type.unit}`}</span>
        </button>
      ))}
      {hidden.length > 0 && (
        <TextLink className={styles.chipsMore} nowrap aria-expanded={expanded} onClick={() => setExpandedFor(expanded ? null : type.id)}>
          {expanded ? 'Vis færre' : `+ ${hidden.length} flere`}
        </TextLink>
      )}
    </div>
  );
}
